import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import AdminLayout from '../../components/AdminLayout';

export default function AdminDirectory() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Same form data structure as DirectorySubmit.jsx
  const [formData, setFormData] = useState({
    company_name: '',
    category: '',
    description_fr: '',
    description_en: '',
    country: '',
    city: '',
    address: '',
    postal_code: '',
    phone: '',
    email: '',
    website: '',
    logo: '',
    services: [],
    certifications: [],
    company_size: ''
  });

  useEffect(() => {
    if (user && user.id) {
      fetchEntries();
      fetchCategories();
      fetchCountries();
    }
  }, [user]);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/directory/admin/all?admin_id=${user.id}`);
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching directory:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/directory/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/directory/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const toggleService = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const toggleCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error(i18n.language === 'fr' ? 'Fichier trop volumineux (max 5MB)' : 'File too large');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error(i18n.language === 'fr' ? 'Le fichier doit être une image' : 'Must be an image');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/directory/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, logo: response.data.logo_url }));
      toast.success(i18n.language === 'fr' ? 'Logo uploadé' : 'Logo uploaded');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Error uploading logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Ensure required fields
      if (!formData.company_name || !formData.category) {
        toast.error('Required fields missing');
        return;
      }

      await axios.post(`${API}/directory/admin/create?admin_id=${user.id}`, formData);
      
      toast.success(i18n.language === 'fr' 
        ? 'Fiche créée et approuvée avec succès' 
        : 'Entry created and approved successfully');
        
      setDialogOpen(false);
      // Reset form
      setFormData({
        company_name: '',
        category: '',
        description_fr: '',
        description_en: '',
        country: '',
        city: '',
        address: '',
        postal_code: '',
        phone: '',
        email: '',
        website: '',
        logo: '',
        services: [],
        certifications: [],
        company_size: ''
      });
      fetchEntries();
    } catch (error) {
      console.error('Error creating directory entry:', error);
      toast.error(error.response?.data?.detail || 'Error creating directory entry');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        await axios.delete(`${API}/directory/admin/delete/${id}?admin_id=${user.id}`);
        toast.success('Entry deleted successfully');
        fetchEntries();
      } catch (error) {
        console.error('Error deleting entry:', error);
        toast.error('Error deleting entry');
      }
    }
  };

  return (
    <AdminLayout>
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-directory">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-[#0A2540]">{t('admin.directory')}</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#635BFF]" data-testid="add-directory-btn">
                <FaPlus className="mr-2" /> {i18n.language === 'fr' ? 'Ajouter une fiche' : 'Add Entry'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto max-w-3xl">
              <DialogHeader>
                <DialogTitle>{i18n.language === 'fr' ? 'Ajouter une entreprise' : 'Add Directory Entry'}</DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Company Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg text-[#0A2540] border-b pb-2">
                    {i18n.language === 'fr' ? 'Informations de l\'entreprise' : 'Company Information'}
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>
                        {i18n.language === 'fr' ? 'Nom de l\'entreprise' : 'Company Name'} *
                      </Label>
                      <Input
                        required
                        value={formData.company_name}
                        onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      />
                    </div>
                    <div>
                        <Label>
                            {i18n.language === 'fr' ? 'Catégorie' : 'Category'} *
                        </Label>
                        <Select 
                            value={formData.category} 
                            onValueChange={(value) => setFormData({...formData, category: value})}
                        >
                            <SelectTrigger>
                            <SelectValue placeholder={i18n.language === 'fr' ? 'Sélectionner' : 'Select'} />
                            </SelectTrigger>
                            <SelectContent>
                            {categories.map(cat => (
                                <SelectItem key={cat.id} value={cat.id}>
                                {i18n.language === 'fr' ? cat.name_fr : cat.name_en}
                                </SelectItem>
                            ))}
                            </SelectContent>
                        </Select>
                    </div>
                  </div>

                  <div>
                    <Label>{i18n.language === 'fr' ? 'Logo' : 'Logo'}</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                    />
                    {uploadingLogo && <p className="text-xs text-[#635BFF]">Upload...</p>}
                    {formData.logo && (
                      <img 
                        src={`${API}${formData.logo}`} 
                        alt="Logo preview" 
                        className="w-20 h-20 object-contain border rounded mt-2"
                      />
                    )}
                  </div>

                  <div>
                    <Label>Description (Français) *</Label>
                    <textarea
                        required
                        rows={3}
                        value={formData.description_fr}
                        onChange={(e) => setFormData({...formData, description_fr: e.target.value})}
                        className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                  <div>
                    <Label>Description (English)</Label>
                    <textarea
                        rows={3}
                        value={formData.description_en}
                        onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                        className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg text-[#0A2540] border-b pb-2">
                    {i18n.language === 'fr' ? 'Localisation' : 'Location'}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{i18n.language === 'fr' ? 'Pays' : 'Country'} *</Label>
                      <Select 
                        value={formData.country} 
                        onValueChange={(value) => setFormData({...formData, country: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={i18n.language === 'fr' ? 'Sélectionner' : 'Select'} />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>{i18n.language === 'fr' ? 'Ville' : 'City'} *</Label>
                      <Input
                        required
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label>{i18n.language === 'fr' ? 'Adresse' : 'Address'}</Label>
                        <Input
                            value={formData.address}
                            onChange={(e) => setFormData({...formData, address: e.target.value})}
                        />
                    </div>
                    <div>
                        <Label>{i18n.language === 'fr' ? 'Code postal' : 'Postal Code'}</Label>
                        <Input
                            value={formData.postal_code}
                            onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                        />
                    </div>
                  </div>
                </div>

                {/* Contact */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg text-[#0A2540] border-b pb-2">
                    {i18n.language === 'fr' ? 'Contact' : 'Contact'}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{i18n.language === 'fr' ? 'Téléphone' : 'Phone'} *</Label>
                      <Input
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>{i18n.language === 'fr' ? 'Site web' : 'Website'}</Label>
                    <Input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                    />
                  </div>
                </div>

                {/* Services & Certifications */}
                <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-[#0A2540] border-b pb-2">
                        {i18n.language === 'fr' ? 'Services & Certifications' : 'Services & Certifications'}
                    </h3>
                    
                    <div>
                        <Label className="mb-2 block">{i18n.language === 'fr' ? 'Types de services' : 'Service Types'}</Label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {['vente', 'location', 'maintenance', 'formation'].map(service => (
                            <div key={service} className="flex items-center space-x-2">
                            <Checkbox
                                checked={formData.services.includes(service)}
                                onCheckedChange={() => toggleService(service)}
                            />
                            <Label className="cursor-pointer capitalize">{service}</Label>
                            </div>
                        ))}
                        </div>
                    </div>

                    <div>
                        <Label className="mb-2 block mt-4">{i18n.language === 'fr' ? 'Certifications' : 'Certifications'}</Label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {['ISO', 'CE', 'NF', 'Autre'].map(cert => (
                            <div key={cert} className="flex items-center space-x-2">
                            <Checkbox
                                checked={formData.certifications.includes(cert)}
                                onCheckedChange={() => toggleCertification(cert)}
                            />
                            <Label className="cursor-pointer">{cert}</Label>
                            </div>
                        ))}
                        </div>
                    </div>
                    
                    <div className="mt-4">
                        <Label>{i18n.language === 'fr' ? 'Taille de l\'entreprise' : 'Company Size'}</Label>
                        <Select value={formData.company_size} onValueChange={(value) => setFormData({...formData, company_size: value})}>
                        <SelectTrigger>
                            <SelectValue placeholder={i18n.language === 'fr' ? 'Sélectionner' : 'Select'} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="artisan">{i18n.language === 'fr' ? 'Artisan' : 'Craftsman'}</SelectItem>
                            <SelectItem value="pme">PME</SelectItem>
                            <SelectItem value="grand_groupe">{i18n.language === 'fr' ? 'Grand groupe' : 'Large Group'}</SelectItem>
                        </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('admin.cancel')}</Button>
                  <Button type="submit" className="bg-[#0A2540]" data-testid="save-directory-btn">{t('admin.save')}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {entries.map((entry, index) => (
            <Card key={entry.id} data-testid={`directory-entry-${index}`}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-bold text-xl text-[#0A2540]">{entry.company_name}</h3>
                    <span className={`text-xs px-2 py-1 rounded ${entry.status === 'approved' ? 'bg-[#635BFF]/10 text-[#5248d9]' : 'bg-yellow-100 text-yellow-800'}`}>
                      {entry.status}
                    </span>
                  </div>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(entry.id)} data-testid={`delete-entry-${index}`}>
                    <FaTrash />
                  </Button>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  {i18n.language === 'fr' ? entry.description_fr : entry.description_en}
                </p>
                <div className="text-sm space-y-1">
                  <p><strong>Email:</strong> {entry.email}</p>
                  <p><strong>Phone:</strong> {entry.phone}</p>
                  {entry.website && <p><strong>Website:</strong> {entry.website}</p>}
                  <p><strong>Loc:</strong> {entry.city}, {entry.country}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
    </AdminLayout>
  );
}
